/*
 * Copyright 2026 North Carolina State University
 */

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert, Autocomplete, Box, Button, Chip, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle,
  FormControlLabel, IconButton, List, ListItemButton, ListItemText, MenuItem, Paper, Switch,
  TextField, Tooltip, Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import { toast } from 'react-toastify';
import { ipmApi, errorMessage } from '../api';
import { newId, typeInfo } from '../fieldTypes';
import FieldEditorDialog from './FieldEditorDialog';

const blankForm = (kind) => ({
  _id: null,
  kind,
  name: 'New form',
  description: '',
  active: true,
  entry_date_field: null,
  title_field: null,
  summary: { show: false, mode: 'recent', recent_days: 14, per_group: 5, display_fields: [] },
  sections: [{ id: newId('s'), title: 'Details', description: '', fields: [] }],
});

const draftOf = (form) => JSON.parse(JSON.stringify(form));
const comparable = (f) => JSON.stringify({
  name: f.name, description: f.description, active: f.active, entry_date_field: f.entry_date_field,
  title_field: f.title_field, summary: f.summary, sections: f.sections,
});

const SUMMARY_GROUP_TYPES = ['chamber', 'project', 'person', 'control'];

const swap = (arr, i, j) => {
  const out = [...arr];
  [out[i], out[j]] = [out[j], out[i]];
  return out;
};

/**
 * Where admins define the IPM forms: sections, fields, types, required.
 * Changes are saved as a new version of the form; existing entries keep the
 * fields they were saved with.
 */
const FormBuilder = ({ kind = 'entry', onClose, onChanged, initialFormId, backLabel = 'Back' }) => {
  const isControls = kind === 'controls';
  const [forms, setForms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [draft, setDraft] = useState(null);
  const [original, setOriginal] = useState(null);
  const [saving, setSaving] = useState(false);
  const [fieldEdit, setFieldEdit] = useState(null); // { sectionIdx, fieldIdx|null, field }
  const [confirm, setConfirm] = useState(null); // { title, body, action, label, danger }

  const dirty = draft && original && comparable(draft) !== comparable(original);
  const savedForm = draft?._id ? forms.find(f => f._id === draft._id) : null;
  const hasEntries = (savedForm?.entry_count || 0) > 0;

  const open = useCallback((form) => {
    const d = draftOf(form);
    setDraft(d);
    setOriginal(draftOf(form));
  }, []);

  const load = useCallback(async (selectId) => {
    try {
      const list = await ipmApi.forms(kind, true);
      setForms(list);
      const pick = list.find(f => f._id === selectId) || list.find(f => !f.archived) || list[0];
      if (pick) open(pick); else { setDraft(null); setOriginal(null); }
    } catch (err) {
      toast.error(errorMessage(err, 'Could not load forms'));
    } finally {
      setLoading(false);
    }
  }, [open, kind]);

  useEffect(() => { load(initialFormId); }, [load, initialFormId]);

  // Number fields on the List of Controls that a field here can auto-fill from (e.g. REI).
  const [controlForms, setControlForms] = useState([]);
  useEffect(() => {
    if (isControls) return;
    ipmApi.forms('controls').then(setControlForms).catch(() => {});
  }, [isControls]);
  const controlAttributes = useMemo(() => controlForms.flatMap(cf => cf.sections.flatMap(s => s.fields))
    .filter(f => f.type === 'number')
    .map(f => ({ value: f.id, label: f.label })), [controlForms]);
  const autofillSources = useMemo(() => (draft?.sections || []).flatMap(s => s.fields).flatMap(f => {
    if (f.type === 'control') return [{ value: f.id, label: f.label }];
    if (f.type === 'table') return (f.columns || []).filter(c => c.type === 'control').map(c => ({ value: `${f.id}.${c.id}`, label: `${f.label} → ${c.label}` }));
    return [];
  }), [draft]);

  const moveForm = async (i, dir) => {
    const ids = forms.map(f => f._id);
    [ids[i], ids[i + dir]] = [ids[i + dir], ids[i]];
    try {
      await ipmApi.reorderForms(ids);
      setForms(prev => ids.map(id => prev.find(f => f._id === id)));
      onChanged?.();
    } catch (err) { toast.error(errorMessage(err, 'Could not reorder the forms')); }
  };

  const guard = (action) => {
    if (!dirty) return action();
    setConfirm({ title: 'Discard unsaved changes?', body: `Your changes to “${draft.name}” have not been saved.`, label: 'Discard', danger: true, action });
  };

  /* ── draft edits ── */
  const set = (patch) => setDraft(prev => ({ ...prev, ...patch }));
  const setSections = (fn) => setDraft(prev => ({ ...prev, sections: fn(prev.sections) }));
  const setSection = (i, patch) => setSections(ss => ss.map((s, idx) => (idx === i ? { ...s, ...patch } : s)));

  const moveField = (si, fi, dir) => setSections((ss) => {
    const next = ss.map(s => ({ ...s, fields: [...s.fields] }));
    const target = fi + dir;
    if (target >= 0 && target < next[si].fields.length) {
      next[si].fields = swap(next[si].fields, fi, target);
    } else if (next[si + dir]) {
      // Past the end of a section: carry the field into the neighbouring one.
      const [fld] = next[si].fields.splice(fi, 1);
      if (dir < 0) next[si - 1].fields.push(fld); else next[si + 1].fields.unshift(fld);
    }
    return next;
  });

  const removeField = (si, fi) => setSections(ss => ss.map((s, idx) => (idx === si ? { ...s, fields: s.fields.filter((_, j) => j !== fi) } : s)));

  const saveField = (field) => {
    const { sectionIdx, fieldIdx } = fieldEdit;
    setSections(ss => ss.map((s, idx) => {
      if (idx !== sectionIdx) return s;
      const fields = [...s.fields];
      if (fieldIdx === null) fields.push(field); else fields[fieldIdx] = field;
      return { ...s, fields };
    }));
    setFieldEdit(null);
  };

  const allFields = useMemo(() => (draft?.sections || []).flatMap(s => s.fields), [draft]);
  const dateFields = allFields.filter(f => f.type === 'date');
  const ofType = (...types) => allFields.filter(f => types.includes(f.type));
  const summary = draft?.summary || {};
  const setSummary = (patch) => set({ summary: { ...summary, ...patch } });

  /* ── persistence ── */
  const save = async () => {
    if (!draft.name.trim()) return toast.error('Give the form a name.');
    const emptyLabel = draft.sections.some(s => s.fields.some(f => !f.label?.trim()));
    if (emptyLabel) return toast.error('Every field needs a label.');
    setSaving(true);
    try {
      const body = { ...draft, entry_date_field: draft.entry_date_field || dateFields[0]?.id || null };
      const saved = draft._id ? await ipmApi.saveForm(draft._id, body) : await ipmApi.createForm(body);
      toast.success(`“${saved.name}” saved`);
      await load(saved._id);
      onChanged?.();
    } catch (err) {
      toast.error(errorMessage(err, 'Could not save the form'));
    } finally {
      setSaving(false);
    }
  };

  const remove = () => setConfirm({
    title: hasEntries ? `Archive “${draft.name}”?` : `Delete “${draft.name}”?`,
    body: hasEntries
      ? `This form has ${savedForm.entry_count} entr${savedForm.entry_count === 1 ? 'y' : 'ies'}, so it will be archived instead of deleted. Its entries are kept and it can be restored later.`
      : 'This form has no entries and will be permanently deleted.',
    label: hasEntries ? 'Archive' : 'Delete',
    danger: true,
    action: async () => {
      try {
        await ipmApi.deleteForm(draft._id);
        toast.success(hasEntries ? 'Form archived' : 'Form deleted');
        await load();
        onChanged?.();
      } catch (err) { toast.error(errorMessage(err, 'Could not remove the form')); }
    },
  });

  const restore = async () => {
    try {
      await ipmApi.saveForm(draft._id, { ...original, archived: false, active: true });
      toast.success('Form restored');
      await load(draft._id);
      onChanged?.();
    } catch (err) { toast.error(errorMessage(err, 'Could not restore the form')); }
  };

  const addStarter = async () => {
    try {
      const created = await ipmApi.createStarterForms(kind);
      toast.success(created.length ? `Added ${created.map(f => f.name).join(', ')}` : 'Those forms already exist');
      await load(created[0]?._id);
      onChanged?.();
    } catch (err) { toast.error(errorMessage(err, 'Could not add the starter forms')); }
  };

  if (loading) return <CircularProgress />;

  return (
    <Box>
      <Box className="ipm-page-header ipm-builder-header">
        <Box>
          <h2>{isControls ? 'Manage the List of Controls form' : 'Manage forms'}</h2>
          <p>
            {isControls ? 'Choose what is recorded for each control.' : 'Build the forms used for data entry.'}
            {' '}Saving a change starts a new version; earlier entries keep the fields they were saved with.
          </p>
        </Box>
        <Button startIcon={<ArrowBackIcon />} onClick={() => guard(onClose)}>{backLabel}</Button>
      </Box>

      <Box className="ipm-builder">
        {/* ── form list ── */}
        <Paper variant="outlined" className="ipm-builder-list">
          <List dense disablePadding>
            {forms.map((f, i) => (
              <ListItemButton key={f._id} selected={draft?._id === f._id} onClick={() => guard(() => open(f))}
                sx={{ pr: forms.length > 1 ? 9 : 2, position: 'relative' }}>
                <ListItemText
                  primary={f.name}
                  secondary={[
                    `${f.entry_count} entr${f.entry_count === 1 ? 'y' : 'ies'}`,
                    f.archived ? 'archived' : !f.active ? 'hidden' : null,
                  ].filter(Boolean).join(' · ')}
                />
                {forms.length > 1 && (
                  <Box className="ipm-form-order" onClick={e => e.stopPropagation()}>
                    <IconButton size="small" aria-label={`Move ${f.name} up`} disabled={i === 0} onClick={() => moveForm(i, -1)}><ArrowUpwardIcon fontSize="inherit" /></IconButton>
                    <IconButton size="small" aria-label={`Move ${f.name} down`} disabled={i === forms.length - 1} onClick={() => moveForm(i, 1)}><ArrowDownwardIcon fontSize="inherit" /></IconButton>
                  </Box>
                )}
              </ListItemButton>
            ))}
            {draft && !draft._id && (
              <ListItemButton selected><ListItemText primary={draft.name || 'New form'} secondary="not saved yet" /></ListItemButton>
            )}
          </List>
          <Box sx={{ p: 1, display: 'flex', flexDirection: 'column', gap: 1 }}>
            <Button startIcon={<AddIcon />} variant="outlined" onClick={() => guard(() => open(blankForm(kind)))}>New form</Button>
            <Button size="small" onClick={() => guard(addStarter)}>
              {isControls ? 'Add the starter List of Controls form' : 'Add starter forms (Quarantine, Scouting, Treatment)'}
            </Button>
            {forms.length > 1 && <Typography variant="caption" className="ipm-muted">Arrows set the order of {isControls ? 'the tabs' : 'the tabs and Summary columns'}.</Typography>}
          </Box>
        </Paper>

        {/* ── editor ── */}
        <Box sx={{ flex: 1, minWidth: 0 }}>
          {!draft && <Alert severity="info">No forms yet. Create one, or add the starter forms to begin with.</Alert>}
          {draft && (
            <>
              {draft.archived && (
                <Alert severity="warning" sx={{ mb: 2 }} action={<Button color="inherit" size="small" onClick={restore}>Restore</Button>}>
                  This form is archived. Its entries are kept, but it is not offered for data entry.
                </Alert>
              )}
              <Paper variant="outlined" className="ipm-section">
                <Box className="ipm-field-grid">
                  <TextField label="Form name" required value={draft.name} onChange={e => set({ name: e.target.value })} />
                  {isControls ? (
                    <TextField select label="Field that names each control" value={draft.title_field || ofType('text')[0]?.id || ''}
                      onChange={e => set({ title_field: e.target.value })} disabled={!ofType('text').length}
                      helperText="Shown when a control is picked on a data-entry form.">
                      {ofType('text').map(f => <MenuItem key={f.id} value={f.id}>{f.label}</MenuItem>)}
                    </TextField>
                  ) : (
                    <TextField select label="Date that files each entry" value={draft.entry_date_field || dateFields[0]?.id || ''}
                      onChange={e => set({ entry_date_field: e.target.value })} disabled={!dateFields.length}
                      helperText={dateFields.length ? 'Used to sort entries and in the Summary.' : 'Add a date field to choose one; until then the save time is used.'}>
                      {dateFields.map(f => <MenuItem key={f.id} value={f.id}>{f.label}</MenuItem>)}
                    </TextField>
                  )}
                  <TextField className="ipm-field-full" label="Description (optional)" value={draft.description}
                    onChange={e => set({ description: e.target.value })} />
                  <FormControlLabel control={<Switch checked={draft.active !== false} onChange={e => set({ active: e.target.checked })} />}
                    label={isControls ? 'Show on the List of Controls page' : 'Show on the Data Entry page'} />
                </Box>
              </Paper>

              {!isControls && (
                <Paper variant="outlined" className="ipm-section">
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap' }}>
                    <Typography variant="h6" className="ipm-section-title" sx={{ mb: '0 !important' }}>Summary settings</Typography>
                    <FormControlLabel control={<Switch checked={!!summary.show} onChange={e => setSummary({ show: e.target.checked })} />}
                      label="Show as a column on the Summary page" />
                  </Box>
                  {summary.show && (
                    <Box className="ipm-field-grid" sx={{ mt: 2 }}>
                      <TextField select label="Group entries by" value={summary.group_field || ''}
                        onChange={e => setSummary({ group_field: e.target.value || null })}
                        helperText="One card per compartment, project, etc.">
                        <MenuItem value=""><em>Don’t group</em></MenuItem>
                        {ofType(...SUMMARY_GROUP_TYPES).map(f => <MenuItem key={f.id} value={f.id}>{f.label}</MenuItem>)}
                      </TextField>
                      <TextField select label="Counts as ongoing while" value={summary.mode || 'recent'}
                        onChange={e => setSummary({ mode: e.target.value })}>
                        <MenuItem value="recent">Entries keep being added</MenuItem>
                        <MenuItem value="window">Its start date + duration hasn’t passed</MenuItem>
                      </TextField>
                      {summary.mode === 'window' ? (
                        <>
                          <TextField select label="Start date" value={summary.start_field || ''}
                            onChange={e => setSummary({ start_field: e.target.value || null })}
                            helperText="Read from the latest entry of each group.">
                            <MenuItem value=""><em>The entry’s filing date</em></MenuItem>
                            {dateFields.map(f => <MenuItem key={f.id} value={f.id}>{f.label}</MenuItem>)}
                          </TextField>
                          <TextField select label="Duration" value={summary.duration_field || ''}
                            onChange={e => setSummary({ duration_field: e.target.value || null })}
                            helperText="A number field; units of days, weeks or months are understood. Blank = ongoing until set.">
                            <MenuItem value=""><em>None — always ongoing</em></MenuItem>
                            {ofType('number').map(f => <MenuItem key={f.id} value={f.id}>{f.label}</MenuItem>)}
                          </TextField>
                        </>
                      ) : (
                        <TextField type="number" label="Ongoing if there is an entry in the last … days" value={summary.recent_days ?? 14}
                          onChange={e => setSummary({ recent_days: e.target.value })} inputProps={{ min: 1, max: 365 }} />
                      )}
                      <TextField type="number" label="Entries shown per card" value={summary.per_group ?? 5}
                        onChange={e => setSummary({ per_group: e.target.value })} inputProps={{ min: 1, max: 20 }} />
                      <TextField select label="Next check date (optional)" value={summary.next_date_field || ''}
                        onChange={e => setSummary({ next_date_field: e.target.value || null })}
                        helperText="Cards are then sorted soonest first, and overdue checks stand out.">
                        <MenuItem value=""><em>None</em></MenuItem>
                        {dateFields.map(f => <MenuItem key={f.id} value={f.id}>{f.label}</MenuItem>)}
                      </TextField>
                      <TextField select label="Next check time (optional)" value={summary.next_time_field || ''}
                        onChange={e => setSummary({ next_time_field: e.target.value || null })}>
                        <MenuItem value=""><em>None</em></MenuItem>
                        {ofType('time').map(f => <MenuItem key={f.id} value={f.id}>{f.label}</MenuItem>)}
                      </TextField>
                      <Autocomplete
                        className="ipm-field-full" multiple
                        options={allFields.filter(f => !['images', 'files'].includes(f.type))}
                        getOptionLabel={f => f.label}
                        value={allFields.filter(f => (summary.display_fields || []).includes(f.id))}
                        isOptionEqualToValue={(o, v) => o.id === v.id}
                        onChange={(e, v) => setSummary({ display_fields: v.map(f => f.id) })}
                        renderInput={params => <TextField {...params} label="Show on each entry" helperText="Besides the date and who entered it." />}
                      />
                    </Box>
                  )}
                </Paper>
              )}

              {draft.sections.map((s, si) => (
                <Paper key={s.id} variant="outlined" className="ipm-section">
                  <Box className="ipm-builder-section-head">
                    <TextField size="small" label="Section title" value={s.title} sx={{ flex: 1 }}
                      onChange={e => setSection(si, { title: e.target.value })} />
                    <Tooltip title="Move section up"><span><IconButton aria-label="Move section up" disabled={si === 0}
                      onClick={() => setSections(ss => swap(ss, si, si - 1))}><ArrowUpwardIcon fontSize="small" /></IconButton></span></Tooltip>
                    <Tooltip title="Move section down"><span><IconButton aria-label="Move section down" disabled={si === draft.sections.length - 1}
                      onClick={() => setSections(ss => swap(ss, si, si + 1))}><ArrowDownwardIcon fontSize="small" /></IconButton></span></Tooltip>
                    <Tooltip title={s.fields.length ? 'Remove or move its fields first' : 'Delete section'}><span>
                      <IconButton aria-label="Delete section" disabled={s.fields.length > 0}
                        onClick={() => setSections(ss => ss.filter((_, idx) => idx !== si))}><DeleteOutlineIcon fontSize="small" /></IconButton>
                    </span></Tooltip>
                  </Box>

                  {s.fields.length === 0 && <Typography className="ipm-muted" sx={{ my: 1 }}>No fields in this section yet.</Typography>}
                  {s.fields.map((f, fi) => (
                    <Box key={f.id} className="ipm-builder-field">
                      <Box sx={{ flex: 1, minWidth: 0 }}>
                        <strong>{f.label}</strong>
                        {f.help && <Typography variant="body2" className="ipm-muted" noWrap>{f.help}</Typography>}
                      </Box>
                      <Chip size="small" label={typeInfo(f.type).label} />
                      {f.required && <Chip size="small" color="primary" variant="outlined" label="Required" />}
                      <Tooltip title="Move up"><span><IconButton size="small" aria-label="Move field up"
                        disabled={si === 0 && fi === 0} onClick={() => moveField(si, fi, -1)}><ArrowUpwardIcon fontSize="small" /></IconButton></span></Tooltip>
                      <Tooltip title="Move down"><span><IconButton size="small" aria-label="Move field down"
                        disabled={si === draft.sections.length - 1 && fi === s.fields.length - 1} onClick={() => moveField(si, fi, 1)}><ArrowDownwardIcon fontSize="small" /></IconButton></span></Tooltip>
                      <Tooltip title="Edit field"><IconButton size="small" aria-label="Edit field"
                        onClick={() => setFieldEdit({ sectionIdx: si, fieldIdx: fi, field: f })}><EditOutlinedIcon fontSize="small" /></IconButton></Tooltip>
                      <Tooltip title="Remove field (earlier entries keep their values)"><IconButton size="small" aria-label="Remove field"
                        onClick={() => removeField(si, fi)}><DeleteOutlineIcon fontSize="small" /></IconButton></Tooltip>
                    </Box>
                  ))}
                  <Button size="small" startIcon={<AddIcon />} sx={{ mt: 1 }}
                    onClick={() => setFieldEdit({ sectionIdx: si, fieldIdx: null, field: { id: newId('f'), label: '', type: 'text', required: false, width: 'half', help: '' } })}>
                    Add field
                  </Button>
                </Paper>
              ))}

              <Button startIcon={<AddIcon />} onClick={() => setSections(ss => [...ss, { id: newId('s'), title: 'New section', description: '', fields: [] }])}>
                Add section
              </Button>

              <Box className="ipm-builder-actions">
                {draft._id && !draft.archived && <Button color="error" onClick={remove}>{hasEntries ? 'Archive form' : 'Delete form'}</Button>}
                <Box sx={{ flex: 1 }} />
                {dirty && <Typography variant="body2" className="ipm-muted" sx={{ alignSelf: 'center' }}>Unsaved changes</Typography>}
                <Button disabled={!dirty || saving} onClick={() => (draft._id ? open(original) : load())}>Discard</Button>
                <Button variant="contained" disabled={(!dirty && !!draft._id) || saving} onClick={save}
                  startIcon={saving ? <CircularProgress size={16} color="inherit" /> : null}>Save form</Button>
              </Box>
            </>
          )}
        </Box>
      </Box>

      <FieldEditorDialog
        open={!!fieldEdit}
        field={fieldEdit?.field}
        isNew={fieldEdit?.fieldIdx === null}
        formHasEntries={hasEntries}
        autofillSources={autofillSources}
        controlAttributes={controlAttributes}
        projectFields={allFields.filter(f => f.type === 'project')}
        onSave={saveField}
        onClose={() => setFieldEdit(null)}
      />

      <Dialog open={!!confirm} onClose={() => setConfirm(null)}>
        <DialogTitle>{confirm?.title}</DialogTitle>
        <DialogContent>{confirm?.body}</DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirm(null)}>Cancel</Button>
          <Button variant="contained" color={confirm?.danger ? 'error' : 'primary'}
            onClick={() => { const a = confirm.action; setConfirm(null); a(); }}>{confirm?.label}</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default FormBuilder;
